import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const NAV_LINKS = [
  { to: '/features', label: 'Features' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/faq', label: 'FAQ' },
];

const PLANS = [
  {
    name: 'Solo Agent',
    desc: 'For independent agents getting started',
    monthly: 29,
    annual: 24,
    color: '#64748B',
    features: [
      'Chrome Extension autofill',
      'Up to 500 clients',
      '10 carrier portals',
      'Basic CRM & pipeline',
      'Renewal alerts (30-day)',
      'Mobile app access',
      'Email support',
    ],
    notIncluded: ['Team collaboration', 'Advanced automation', 'Manager reporting', 'Dedicated account manager'],
    cta: 'Start Free Trial',
    ctaStyle: 'secondary',
  },
  {
    name: 'Pro Agent',
    desc: 'For serious agents who want to dominate',
    monthly: 69,
    annual: 57,
    color: '#2563EB',
    featured: true,
    features: [
      'Everything in Solo',
      'Unlimited clients',
      '40+ carrier portals',
      'Advanced CRM + automation',
      'Renewal alerts (90 / 60 / 30-day)',
      'Pipeline revenue forecasting',
      'Team collaboration (up to 3)',
      'Priority support',
      'CSV + CRM imports',
    ],
    notIncluded: ['Manager permissions & roles', 'Dedicated account manager', 'Custom integrations'],
    cta: 'Start Free Trial',
    ctaStyle: 'primary',
    badge: '⭐ Most Popular',
  },
  {
    name: 'Agency Team',
    desc: 'For growing agencies with multiple agents',
    monthly: 149,
    annual: 124,
    color: '#7C3AED',
    features: [
      'Everything in Pro',
      'Unlimited agents',
      'Team dashboard & reporting',
      'Manager permissions & roles',
      'Renewal leaderboard',
      'Custom integrations',
      'Open API access',
      'Dedicated account manager',
      'SOC 2 compliance docs',
      'Priority phone support',
    ],
    notIncluded: [],
    cta: 'Start Free Trial',
    ctaStyle: 'secondary',
  },
];

const COMPARE_ROWS = [
  { label: 'Chrome Extension autofill', solo: true, pro: true, agency: true },
  { label: 'Client records', solo: '500', pro: 'Unlimited', agency: 'Unlimited' },
  { label: 'Carrier portals', solo: '10', pro: '40+', agency: '40+' },
  { label: 'Renewal alerts', solo: '30-day only', pro: '90/60/30-day', agency: '90/60/30-day' },
  { label: 'Mobile app', solo: true, pro: true, agency: true },
  { label: 'Pipeline dashboard', solo: true, pro: true, agency: true },
  { label: 'Revenue forecasting', solo: false, pro: true, agency: true },
  { label: 'Team collaboration', solo: false, pro: '3 agents', agency: 'Unlimited' },
  { label: 'Manager permissions', solo: false, pro: false, agency: true },
  { label: 'Custom integrations / API', solo: false, pro: false, agency: true },
  { label: 'Dedicated account manager', solo: false, pro: false, agency: true },
  { label: 'SOC 2 compliance docs', solo: false, pro: false, agency: true },
  { label: 'Support', solo: 'Email', pro: 'Priority email', agency: 'Phone + email' },
];

const FAQS = [
  { q: 'Can I switch plans at any time?', a: 'Yes — upgrade or downgrade anytime from your account settings. Upgrades are prorated immediately. Downgrades take effect at the next billing cycle.' },
  { q: 'What happens after the 14-day trial?', a: 'You\'ll be prompted to choose a plan. If you don\'t, your account moves to read-only mode — no data is deleted. You can reactivate anytime.' },
  { q: 'Do you offer discounts for annual billing?', a: 'Yes — annual billing saves you 2 months compared to monthly. Prices shown toggle between monthly and annual.' },
  { q: 'Is there a setup fee?', a: 'Never. No setup fee, no onboarding fee, no per-carrier charges. The price you see is what you pay.' },
  { q: 'What payment methods do you accept?', a: 'All major credit and debit cards (Visa, Mastercard, Amex, Discover). Agency plans can also pay by ACH bank transfer or invoice.' },
];

function SharedNav({ mobileOpen, setMobileOpen }) {
  return (
    <>
      <style>{`
        .nav{position:sticky;top:0;z-index:100;background:rgba(255,255,255,0.92);backdrop-filter:blur(16px);border-bottom:1px solid #E2E8F0;padding:0 2rem;height:68px;display:flex;align-items:center;justify-content:space-between;}
        .nav-logo{font-family:'Sora',sans-serif;font-size:1.25rem;font-weight:800;color:#2563EB;letter-spacing:-0.5px;text-decoration:none;display:flex;align-items:center;gap:0.4rem;}
        .nav-links a{color:#475569;font-size:0.9rem;font-weight:500;text-decoration:none;margin-left:2rem;transition:color 0.2s;}
        .nav-links a:hover{color:#2563EB;}
        .btn-nav{background:#2563EB;color:#fff;padding:0.55rem 1.4rem;border-radius:8px;font-size:0.9rem;font-weight:600;text-decoration:none;transition:background 0.2s,transform 0.15s;border:none;cursor:pointer;}
        .btn-nav:hover{background:#1D4ED8;transform:translateY(-1px);}
        .hamburger{display:none;background:none;border:1px solid #E2E8F0;border-radius:8px;padding:0.4rem 0.6rem;cursor:pointer;font-size:1.2rem;}
        @media(max-width:768px){
          .nav-links,.nav-desktop-cta{display:none !important;}
          .hamburger{display:block;}
          .mobile-menu{position:fixed;top:68px;left:0;right:0;background:#fff;border-bottom:1px solid #E2E8F0;padding:1.5rem 2rem;z-index:99;box-shadow:0 8px 32px rgba(0,0,0,0.08);}
          .mobile-menu a{display:block;padding:0.75rem 0;color:#0F172A;font-size:1rem;font-weight:500;text-decoration:none;border-bottom:1px solid #f1f5f9;}
          .mobile-menu a:last-child{border-bottom:none;}
        }
      `}</style>
      <nav className="nav">
        <Link to="/" className="nav-logo">⚡ Prorately</Link>
        <div className="nav-links" style={{ display: 'flex', alignItems: 'center' }}>
          {NAV_LINKS.map(l => <Link key={l.to} to={l.to}>{l.label}</Link>)}
        </div>
        <div className="nav-desktop-cta" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Link to="/login" style={{ color: '#475569', fontWeight: 500, textDecoration: 'none', fontSize: '0.9rem' }}>Login</Link>
          <Link to="/register" className="btn-nav">Start Free Trial</Link>
        </div>
        <button className="hamburger" onClick={() => setMobileOpen(!mobileOpen)}>☰</button>
      </nav>
      {mobileOpen && (
        <div className="mobile-menu">
          {NAV_LINKS.map(l => <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}>{l.label}</Link>)}
          <Link to="/login" onClick={() => setMobileOpen(false)}>Login</Link>
          <Link to="/register" onClick={() => setMobileOpen(false)} style={{ color: '#2563EB', fontWeight: 700 }}>Start Free Trial →</Link>
        </div>
      )}
    </>
  );
}

function Check() { return <span style={{ color: '#2563EB', fontWeight: 700, fontSize: '0.9rem' }}>✓</span>; }
function Cross() { return <span style={{ color: '#CBD5E1', fontSize: '0.9rem' }}>—</span>; }

export default function Pricing() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: '#F8FAFF', color: '#0F172A', overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Sora:wght@400;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        html{scroll-behavior:smooth;}
        .sora{font-family:'Sora',sans-serif;}

        .page-hero{background:linear-gradient(135deg,#1E3A8A 0%,#2563EB 60%,#3B82F6 100%);padding:5rem 2rem 4rem;text-align:center;position:relative;overflow:hidden;}
        .page-hero::before{content:'';position:absolute;inset:0;background-image:linear-gradient(rgba(255,255,255,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.05) 1px,transparent 1px);background-size:40px 40px;}
        .page-hero-content{position:relative;z-index:1;max-width:640px;margin:0 auto;}
        .page-tag{display:inline-block;background:rgba(255,255,255,0.15);border:1px solid rgba(255,255,255,0.3);color:#fff;font-size:0.75rem;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;padding:0.35rem 0.9rem;border-radius:100px;margin-bottom:1.25rem;}
        .page-hero h1{font-family:'Sora',sans-serif;font-size:clamp(2rem,4vw,3rem);font-weight:800;color:#fff;line-height:1.15;letter-spacing:-0.03em;margin-bottom:1rem;}
        .page-hero p{color:rgba(255,255,255,0.8);font-size:1.05rem;line-height:1.7;margin-bottom:2rem;}

        /* Toggle */
        .billing-toggle{display:inline-flex;align-items:center;gap:0.75rem;background:rgba(255,255,255,0.15);border:1px solid rgba(255,255,255,0.25);border-radius:100px;padding:0.4rem 1rem;margin-bottom:0;}
        .toggle-label{font-size:0.88rem;font-weight:600;color:rgba(255,255,255,0.7);cursor:pointer;transition:color 0.2s;}
        .toggle-label.active{color:#fff;}
        .toggle-switch{width:40px;height:22px;background:rgba(255,255,255,0.3);border-radius:100px;position:relative;cursor:pointer;transition:background 0.2s;}
        .toggle-switch.on{background:rgba(255,255,255,0.6);}
        .toggle-knob{position:absolute;top:3px;left:3px;width:16px;height:16px;background:#fff;border-radius:50%;transition:transform 0.2s;box-shadow:0 1px 4px rgba(0,0,0,0.2);}
        .toggle-knob.on{transform:translateX(18px);}
        .save-badge{background:#10B981;color:#fff;font-size:0.7rem;font-weight:700;padding:0.2rem 0.5rem;border-radius:100px;margin-left:0.25rem;}

        /* Plans */
        .plans-section{max-width:1060px;margin:0 auto;padding:4rem 2rem;}
        .plans-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1.5rem;}
        @media(max-width:900px){.plans-grid{grid-template-columns:1fr;max-width:440px;margin:0 auto;}}
        .plan-card{background:#fff;border:1.5px solid #E2E8F0;border-radius:20px;padding:2.25rem 2rem;position:relative;transition:all 0.25s;}
        .plan-card.featured{border-color:#2563EB;box-shadow:0 16px 56px rgba(37,99,235,0.15);}
        .plan-badge{position:absolute;top:-14px;left:50%;transform:translateX(-50%);background:#2563EB;color:#fff;font-size:0.72rem;font-weight:700;padding:0.3rem 1rem;border-radius:100px;white-space:nowrap;}
        .plan-name{font-family:'Sora',sans-serif;font-weight:800;font-size:1rem;color:#0F172A;margin-bottom:0.3rem;}
        .plan-desc{font-size:0.83rem;color:#64748B;margin-bottom:1.25rem;}
        .plan-price{font-family:'Sora',sans-serif;font-size:2.8rem;font-weight:800;color:#0F172A;line-height:1;}
        .plan-price sup{font-size:1.1rem;vertical-align:top;margin-top:0.4rem;display:inline-block;}
        .plan-price sub{font-size:0.9rem;font-weight:500;color:#94A3B8;vertical-align:bottom;}
        .plan-period{color:#94A3B8;font-size:0.82rem;margin:0.35rem 0 1.5rem;}
        .plan-divider{border:none;border-top:1px solid #E2E8F0;margin:1.25rem 0;}
        .plan-features{list-style:none;display:flex;flex-direction:column;gap:0.65rem;margin-bottom:1.75rem;}
        .plan-features li{font-size:0.87rem;color:#475569;display:flex;align-items:flex-start;gap:0.55rem;line-height:1.4;}
        .plan-cta{display:block;text-align:center;padding:0.85rem;border-radius:10px;font-weight:700;font-size:0.95rem;text-decoration:none;transition:all 0.2s;cursor:pointer;border:none;}
        .plan-cta.primary{background:#2563EB;color:#fff;box-shadow:0 4px 16px rgba(37,99,235,0.25);}
        .plan-cta.primary:hover{background:#1D4ED8;transform:translateY(-1px);}
        .plan-cta.secondary{background:#F8FAFF;color:#2563EB;border:1.5px solid #BFDBFE;}
        .plan-cta.secondary:hover{background:#EFF6FF;}

        /* Compare table */
        .compare-section{background:#fff;padding:5rem 2rem;}
        .compare-inner{max-width:900px;margin:0 auto;}
        .compare-header{text-align:center;margin-bottom:2.5rem;}
        .section-tag{display:inline-block;background:#EFF6FF;color:#2563EB;font-size:0.75rem;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;padding:0.35rem 0.9rem;border-radius:100px;border:1px solid #BFDBFE;margin-bottom:1rem;}
        .section-title{font-family:'Sora',sans-serif;font-size:clamp(1.6rem,3vw,2.2rem);font-weight:800;line-height:1.2;letter-spacing:-0.02em;color:#0F172A;}
        .compare-table{width:100%;border-collapse:collapse;font-size:0.88rem;}
        .compare-table th{padding:1rem;font-family:'Sora',sans-serif;font-weight:700;font-size:0.82rem;color:#64748B;text-transform:uppercase;letter-spacing:0.06em;border-bottom:2px solid #E2E8F0;text-align:center;}
        .compare-table th:first-child{text-align:left;}
        .compare-table td{padding:0.85rem 1rem;border-bottom:1px solid #F1F5F9;color:#475569;text-align:center;vertical-align:middle;}
        .compare-table td:first-child{text-align:left;font-weight:500;color:#0F172A;}
        .compare-table tr:hover td{background:#F8FAFF;}
        .col-featured{background:#EFF6FF;}
        @media(max-width:640px){.compare-section{display:none;}}

        /* FAQ */
        .pricing-faq{background:#F8FAFF;padding:5rem 2rem;}
        .pricing-faq-inner{max-width:720px;margin:0 auto;}
        .faq-item{border-bottom:1px solid #E2E8F0;padding:1.5rem 0;}
        .faq-question{display:flex;justify-content:space-between;align-items:center;cursor:pointer;gap:1rem;}
        .faq-q-text{font-weight:600;color:#0F172A;font-size:0.98rem;}
        .faq-chevron{color:#94A3B8;font-size:1rem;transition:transform 0.25s;flex-shrink:0;}
        .faq-chevron.open{transform:rotate(180deg);color:#2563EB;}
        .faq-answer{color:#64748B;font-size:0.92rem;line-height:1.7;max-height:0;overflow:hidden;transition:max-height 0.35s ease,padding-top 0.25s;}
        .faq-answer.open{max-height:200px;padding-top:0.75rem;}

        /* CTA */
        .page-cta{background:#0F172A;padding:5rem 2rem;text-align:center;}
        .page-cta h2{font-family:'Sora',sans-serif;font-size:clamp(1.8rem,3.5vw,2.5rem);font-weight:800;color:#fff;margin-bottom:1rem;letter-spacing:-0.02em;}
        .page-cta p{color:#64748B;font-size:1rem;margin-bottom:2rem;max-width:480px;margin-left:auto;margin-right:auto;line-height:1.7;}
        .btn-blue{background:#2563EB;color:#fff;padding:0.9rem 2rem;border-radius:12px;font-weight:700;font-size:1rem;text-decoration:none;border:none;cursor:pointer;transition:all 0.2s;display:inline-flex;align-items:center;gap:0.5rem;box-shadow:0 4px 16px rgba(37,99,235,0.3);}
        .btn-blue:hover{background:#1D4ED8;transform:translateY(-1px);}

        footer{background:#0F172A;color:#94A3B8;padding:3rem 2rem 2rem;border-top:1px solid #1E293B;}
        .footer-bottom-simple{max-width:1100px;margin:0 auto;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:1rem;font-size:0.82rem;}
        .footer-bottom-simple a{color:#64748B;text-decoration:none;margin-left:1.5rem;}
        .footer-bottom-simple a:hover{color:#93C5FD;}
      `}</style>

      <SharedNav mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <div className="page-hero">
        <div className="page-hero-content">
          <span className="page-tag">Pricing</span>
          <h1 className="sora">Simple pricing for every agent</h1>
          <p>No hidden fees. No per-carrier charges. Every plan includes the Chrome Extension and full CRM. Start free for 14 days.</p>

          {/* Billing toggle */}
          <div className="billing-toggle">
            <span className={`toggle-label${!annual ? ' active' : ''}`} onClick={() => setAnnual(false)}>Monthly</span>
            <div className={`toggle-switch${annual ? ' on' : ''}`} onClick={() => setAnnual(!annual)}>
              <div className={`toggle-knob${annual ? ' on' : ''}`} />
            </div>
            <span className={`toggle-label${annual ? ' active' : ''}`} onClick={() => setAnnual(true)}>
              Annual <span className="save-badge">Save 2 months</span>
            </span>
          </div>
        </div>
      </div>

      {/* Plans */}
      <div className="plans-section">
        <div className="plans-grid">
          {PLANS.map(p => (
            <div className={`plan-card${p.featured ? ' featured' : ''}`} key={p.name}>
              {p.badge && <div className="plan-badge">{p.badge}</div>}
              <div className="plan-name sora">{p.name}</div>
              <div className="plan-desc">{p.desc}</div>
              <div className="plan-price sora">
                <sup>$</sup>{annual ? p.annual : p.monthly}<sub>/mo</sub>
              </div>
              <div className="plan-period">{annual ? 'billed annually' : 'billed monthly'}{annual ? ` · save $${(p.monthly - p.annual) * 12}/yr` : ''}</div>
              <hr className="plan-divider" />
              <ul className="plan-features">
                {p.features.map(f => (
                  <li key={f}><Check />{f}</li>
                ))}
                {p.notIncluded.map(f => (
                  <li key={f} style={{ opacity: 0.4 }}><Cross />{f}</li>
                ))}
              </ul>
              <Link to="/register" className={`plan-cta ${p.ctaStyle}`}>{p.cta}</Link>
            </div>
          ))}
        </div>
        <p style={{ textAlign: 'center', color: '#94A3B8', fontSize: '0.82rem', marginTop: '1.75rem' }}>
          All plans include a 14-day free trial. No credit card required. Cancel anytime.
        </p>
      </div>

      {/* Compare table */}
      <div className="compare-section">
        <div className="compare-inner">
          <div className="compare-header">
            <span className="section-tag">Compare Plans</span>
            <h2 className="section-title sora">See exactly what's included</h2>
          </div>
          <table className="compare-table">
            <thead>
              <tr>
                <th style={{ width: '40%' }}>Feature</th>
                <th>Solo</th>
                <th className="col-featured">Pro</th>
                <th>Agency</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map(row => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  <td>{row.solo === true ? <Check /> : row.solo === false ? <Cross /> : row.solo}</td>
                  <td className="col-featured">{row.pro === true ? <Check /> : row.pro === false ? <Cross /> : row.pro}</td>
                  <td>{row.agency === true ? <Check /> : row.agency === false ? <Cross /> : row.agency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ */}
      <div className="pricing-faq">
        <div className="pricing-faq-inner">
          <span className="section-tag">Billing FAQ</span>
          <h2 className="section-title sora" style={{ marginBottom: '2rem' }}>Questions about pricing</h2>
          {FAQS.map((faq, i) => (
            <div className="faq-item" key={i}>
              <div className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <span className="faq-q-text">{faq.q}</span>
                <span className={`faq-chevron${openFaq === i ? ' open' : ''}`}>▾</span>
              </div>
              <div className={`faq-answer${openFaq === i ? ' open' : ''}`}>{faq.a}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="page-cta">
        <h2 className="sora">Start free. Upgrade when you're ready.</h2>
        <p>14 days free on any plan. No credit card required. Cancel or change plans anytime.</p>
        <Link to="/register" className="btn-blue">Start Free Trial →</Link>
      </div>

      <footer>
        <div className="footer-bottom-simple">
          <span>© {new Date().getFullYear()} Prorately. All rights reserved.</span>
          <div>
            <a href="#">Privacy</a>
            <a href="#">Terms</a>
            <a href="#">Security</a>
          </div>
        </div>
      </footer>
    </div>
  );
}